import {
  ICartProduct,
  PBMVerifyPricesInput,
  PBMVerifyPricesDocument,
  PBMVerifyPricesMiddleware,
  PBMVerifyPricesResponse,
  CartVerifyInput,
  CartVerifyItemResponse,
} from '@/types';
import { findPriceChannelEntry } from '@/lib/utils/product';

export const INTERPLAYERS_MIDDLEWARE_SLUG = 'interplayers';
const DEFAULT_PRICE_CHANNEL_ID = 1;

export function hasPBMProducts(items: ICartProduct[]): boolean {
  return items.some((item) => item.pbm_info?.flag);
}

export function isInterplayersPBMItem(item: ICartProduct): boolean {
  const slug = item.pbm_info?.program?.middleware?.slug?.trim().toLowerCase();
  return Boolean(item.pbm_info?.flag && slug === INTERPLAYERS_MIDDLEWARE_SLUG);
}

export function getInterplayersPBMItems(items: ICartProduct[]): ICartProduct[] {
  return items.filter(isInterplayersPBMItem);
}

export function shouldVerifyPBMCombo(items: ICartProduct[]): boolean {
  return getInterplayersPBMItems(items).length >= 1;
}

export function getPBMBaseUnitPrice(item: ICartProduct): number {
  if (typeof item.pbm_info?.base_sale_price === 'number') {
    return item.pbm_info.base_sale_price;
  }
  if (typeof item.pbm_info?.promotion?.price === 'number') {
    return item.pbm_info.promotion.price;
  }
  return item.sale_price;
}

/** Store catalog unit price (channel 1) — used when stripping PBM after verify failure. */
export function getCatalogSalePrice(item: ICartProduct): number {
  const defaultChannel = findPriceChannelEntry(
    item.product_prices ?? [],
    DEFAULT_PRICE_CHANNEL_ID,
  );
  if (defaultChannel) {
    const value = Number(defaultChannel.sale_price ?? defaultChannel.price);
    if (Number.isFinite(value) && value > 0) {
      return value;
    }
  }
  if (typeof item.price === 'number' && Number.isFinite(item.price) && item.price > 0) {
    return item.price;
  }
  return item.sale_price;
}

export function getInterplayersCartSignature(items: ICartProduct[]): string {
  return JSON.stringify(
    getInterplayersPBMItems(items)
      .map((item) => ({
        sku: item.sku,
        quantity: item.quantity,
        cpf: item.pbm_info?.cpf?.replace(/\D/g, '') ?? '',
      }))
      .sort((a, b) => a.sku.localeCompare(b.sku)),
  );
}

export function hasComboAppliedItems(items: ICartProduct[]): boolean {
  return items.some((item) => item.pbm_info?.combo_applied);
}

export function buildPBMVerifyPricesPayload(
  items: ICartProduct[],
  options?: { useBasePbmUnitPrice?: boolean },
): PBMVerifyPricesInput {
  const documentsMap: Record<
    string,
    Record<string, PBMVerifyPricesMiddleware>
  > = {};

  items
    .filter((item) => item.pbm_info?.flag && item.pbm_info?.cpf && item.pbm_info?.program)
    .forEach((item) => {
      const pbmInfo = item.pbm_info;
      if (!pbmInfo?.cpf || !pbmInfo?.program) {
        return;
      }

      const cpf = pbmInfo.cpf.replace(/\D/g, '');
      if (!cpf) {
        return;
      }

      const program = pbmInfo.program;
      const middlewareId = program?.middleware?.id;
      const middlewareSlug = program?.middleware?.slug?.trim();
      const programId = program?.id;
      if (
        middlewareId == null ||
        !Number.isFinite(middlewareId) ||
        middlewareId < 1 ||
        !middlewareSlug ||
        programId == null ||
        !Number.isFinite(programId) ||
        programId < 1
      ) {
        return;
      }

      const programName = program?.name || 'unknown';

      if (!documentsMap[cpf]) {
        documentsMap[cpf] = {};
      }
      if (!documentsMap[cpf][middlewareSlug]) {
        documentsMap[cpf][middlewareSlug] = {
          id: middlewareId,
          name: middlewareSlug,
          programs: [],
        };
      }

      const unit = options?.useBasePbmUnitPrice
        ? getPBMBaseUnitPrice(item)
        : typeof item.calculated_sale_price === 'number' &&
            Number.isFinite(item.calculated_sale_price)
          ? item.calculated_sale_price
          : typeof item.sale_price === 'number' && Number.isFinite(item.sale_price)
            ? item.sale_price
            : 0;

      documentsMap[cpf][middlewareSlug].programs.push({
        name: programName,
        id: programId,
        sku: item.sku,
        sale_price: unit,
        quantity: item.quantity,
      });
    });

  const documents: PBMVerifyPricesDocument[] = Object.entries(documentsMap).map(
    ([document, middlewareObj]) => ({
      document,
      middlewares: Object.values(middlewareObj),
    }),
  );

  return { documents };
}

export function buildCartVerifyPayload(
  items: ICartProduct[],
): CartVerifyInput | null {
  const interplayersItems = getInterplayersPBMItems(items);
  if (interplayersItems.length < 1) {
    return null;
  }

  const payload = buildPBMVerifyPricesPayload(interplayersItems, {
    useBasePbmUnitPrice: true,
  });
  const documents = payload.documents
    .map((doc) => ({
      ...doc,
      middlewares: doc.middlewares.filter(
        (middleware) => middleware.name === INTERPLAYERS_MIDDLEWARE_SLUG,
      ),
    }))
    .filter((doc) => doc.middlewares.length > 0);

  if (!documents.length) {
    return null;
  }

  return { pbm: { documents } };
}

export function mapCartVerifyPricesBySku(
  prices: CartVerifyItemResponse[],
): Map<string, number> {
  return new Map(
    prices
      .filter(
        (entry) =>
          entry?.sku &&
          typeof entry.sale_price === 'number' &&
          Number.isFinite(entry.sale_price),
      )
      .map((entry) => [entry.sku, entry.sale_price]),
  );
}

export function calculatePBMComboSavings(items: ICartProduct[]): number {
  return getInterplayersPBMItems(items).reduce((total, item) => {
    if (!item.pbm_info?.combo_applied) {
      return total;
    }
    const basePrice = getPBMBaseUnitPrice(item);
    const quantity = item.quantity ?? 1;
    const savings = (basePrice - item.sale_price) * quantity;
    return savings > 0 ? total + savings : total;
  }, 0);
}

export function validatePBMResponse(response: PBMVerifyPricesResponse): {
  isValid: boolean;
  unapprovedItems: Array<{
    document: string;
    middleware: string;
    program: string;
    status: string;
    quantity?: number;
    sku?: string;
  }>;
} {
  const unapprovedItems: Array<{
    document: string;
    middleware: string;
    program: string;
    status: string;
    quantity?: number;
    sku?: string;
  }> = [];

  response.documents.forEach((doc) => {
    doc.middlewares.forEach((middleware) => {
      middleware.programs.forEach((program) => {
        if (program.status !== 'approved') {
          unapprovedItems.push({
            document: doc.document,
            middleware: middleware.name,
            program: program.name,
            status: program.status || 'unknown',
            quantity: program.quantity,
            sku: program.sku,
          });
        }
      });
    });
  });

  return {
    isValid: unapprovedItems.length === 0,
    unapprovedItems,
  };
}
